import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { type FacilityOnboardingStep } from "@src/api/facility";
import { FACILITY_ROLES } from "@src/constants/facility";
import { isAfter } from "date-fns";

interface UseFacilityOnboardingFlagParams {
  workplaceCreationDate?: string;
  onboardingSteps?: Partial<FacilityOnboardingStep>;
  roles: FACILITY_ROLES[];
  isAdmin: boolean;
  isWorkplace: boolean;
}

type UseFacilityOnboardingFlagResult = boolean;

const useFacilityOnboardingFlag = (
  props: UseFacilityOnboardingFlagParams
): UseFacilityOnboardingFlagResult => {
  const { roles, isAdmin, workplaceCreationDate, isWorkplace, onboardingSteps } = props;

  const { minimumWorkplaceCreationDate } = useCbhFlag(CbhFeatureFlag.HCF_ONBOARDING, {
    defaultValue: {},
  });
  const { isEnabled: isWorkerInvitationEnabled } = useCbhFlag(
    CbhFeatureFlag.WORKPLACE_TO_WORKER_REFERRALS,
    {
      defaultValue: {
        isEnabled: false,
      },
    }
  );

  const hasPermission =
    roles.includes(FACILITY_ROLES.ADMIN) || roles.includes(FACILITY_ROLES.SHIFT_MANAGEMENT);

  const hasCompletedVisibleOnboarding =
    onboardingSteps?.stage === "completed" ||
    (onboardingSteps?.postShifts === true &&
      onboardingSteps?.getMobileApp === true &&
      onboardingSteps?.checkInstructions === true &&
      onboardingSteps?.addTeamMembers === true &&
      onboardingSteps?.takeProductTour === true &&
      (!isWorkerInvitationEnabled || onboardingSteps?.hasSentWorkerInvitations === true));

  if (
    isAdmin ||
    !isWorkplace ||
    !hasPermission ||
    hasCompletedVisibleOnboarding ||
    !isDefined(minimumWorkplaceCreationDate) ||
    !isDefined(workplaceCreationDate)
  ) {
    return false;
  }

  return isAfter(new Date(workplaceCreationDate), new Date(minimumWorkplaceCreationDate));
};

export { useFacilityOnboardingFlag };
